import dayjs from 'dayjs';

import {
  getLastWeekDateRange,
  matchDatePreset,
  presetRange,
} from '@/components/filter/DateFilter/datePresets';

import type { TEntityOption } from '@/components/filter/types';

import type { TVisitsDateRange } from '@/pages/Admin/StationEntity/Geofence/utils/dateRange';
import type { TLookup } from '@/types/lookup';
import type { TStationListItem } from '@/types/station';

import { PORTAL_DATE_LOOKBACK_MONTHS } from '@/utils/dateUtils';
import { formatAddress } from '@/utils/stringUtils';

import type { TPortalFilter } from './types';

export const PORTAL_FILTER_STORAGE_KEY = 'portalFilters';

/**
 * Id of the "All Sites" row of the portal site picker. It is a pseudo option, never a real site
 * id: the picker maps it to "no ids" (All sites) and never sends or stores it.
 */
export const ALL_SITES_OPTION_ID = '__all_sites__';

/**
 * Key of the `partner_portal` lookup row (type `hf-core`) that sets the sites the portal starts
 * on. Its `value` is the switch ("true" = on) and `attributes.stations` lists the site ids. The
 * row is global: every partner gets the same list, so ids of other partners simply do not match.
 */
export const DEFAULT_STATIONS_LOOKUP_KEY = 'set_default_stations';

/** Last calendar week. The date chip shows that preset until another range is applied. */
export const getDefaultPortalDateRange = (): Pick<TPortalFilter, 'startDate' | 'endDate'> => {
  const lastWeek = getLastWeekDateRange();

  return {
    startDate: lastWeek.from,
    endDate: lastWeek.till,
  };
};

export const createDefaultPortalFilter = (): TPortalFilter => ({
  ...getDefaultPortalDateRange(),
});

export const getPortalDateBounds = (): {
  minDate: dayjs.Dayjs;
  maxDate: dayjs.Dayjs;
} => ({
  minDate: dayjs().subtract(PORTAL_DATE_LOOKBACK_MONTHS, 'month').startOf('day'),
  maxDate: dayjs().endOf('day'),
});

export const toDay = (date?: string) => (date ? dayjs(date).format('YYYY-MM-DD') : undefined);

export const toIsoStart = (date?: string) =>
  date && dayjs(date).isValid() ? dayjs(date).startOf('day').toISOString() : undefined;

export const toIsoEnd = (date?: string) =>
  date && dayjs(date).isValid() ? dayjs(date).endOf('day').toISOString() : undefined;

export const clampPortalDateRange = (
  startDate?: string,
  endDate?: string,
): Pick<TPortalFilter, 'startDate' | 'endDate'> => {
  // Last year is older than the 6-month window. Keep that calendar year so the request
  // is not rewritten to the earliest allowed day.
  if (matchDatePreset(startDate, endDate) === 'lastYear') {
    const range = presetRange('lastYear');
    return {
      startDate: range.from.startOf('day').toISOString(),
      endDate: range.till.endOf('day').toISOString(),
    };
  }

  const { minDate, maxDate } = getPortalDateBounds();
  let from = startDate && dayjs(startDate).isValid() ? dayjs(startDate).startOf('day') : null;
  let till = endDate && dayjs(endDate).isValid() ? dayjs(endDate).startOf('day') : null;

  if (from) {
    if (minDate && from.isBefore(minDate, 'day')) {
      from = minDate;
    }
    if (from.isAfter(maxDate, 'day')) {
      from = maxDate;
    }
  }

  if (till) {
    if (till.isAfter(maxDate, 'day')) {
      till = maxDate;
    }
    if (minDate && till.isBefore(minDate, 'day')) {
      till = minDate;
    }
  }

  if (from && till && till.isBefore(from, 'day')) {
    till = from;
  }

  return {
    startDate: from ? toIsoStart(from.toISOString()) : undefined,
    endDate: till ? toIsoEnd(till.toISOString()) : undefined,
  };
};

const LIST_COLLECTIONS_WITH_STATION_DATE = ['transactions', 'payments', 'customers'] as const;

const LIST_FILTER_KEYS: Record<typeof LIST_COLLECTIONS_WITH_STATION_DATE[number], string[]> = {
  transactions: ['fuelType', 'fuelGrade', 'customerId', 'transactionId'],
  payments: ['status', 'paymentMethodId'],
  customers: ['fuelType', 'fuelGrade'],
};

const pickKeys = (source: Record<string, unknown>, keys: string[]) => {
  const next: Record<string, unknown> = {};

  keys.forEach((key) => {
    if (source[key] !== undefined) {
      next[key] = source[key];
    }
  });

  return next;
};

export const toVisitsDateRange = (
  startDate?: string,
  endDate?: string,
): TVisitsDateRange | undefined => {
  if (!startDate || !endDate) {
    return undefined;
  }

  return {
    from: dayjs(startDate).startOf('day').toISOString(),
    to: dayjs(endDate).endOf('day').toISOString(),
  };
};

export const uniqueStationIds = (stationId?: string[]): string[] | undefined => {
  if (!stationId?.length) {
    return undefined;
  }

  const ids = [...new Set(stationId.filter(Boolean))];
  return ids.length ? ids : undefined;
};

export const firstStationId = (stationId?: string[]): string[] | undefined => {
  const ids = uniqueStationIds(stationId);
  return ids ? [ids[0]] : undefined;
};

export const mergeStationOptions = (...groups: TEntityOption[][]): TEntityOption[] => {
  const byId = new Map<string, TEntityOption>();
  groups.flat().forEach((station) => byId.set(station.id, station));
  return [...byId.values()];
};

export const toStationOption = ({ id, generalInfo }: TStationListItem): TEntityOption => ({
  id,
  title: generalInfo.name,
  details: formatAddress(generalInfo.location),
});

const overlayStationAndDates = (next: Record<string, unknown>, portal: TPortalFilter) => {
  const stationId = uniqueStationIds(portal.stationId);
  const startDate = toIsoStart(portal.startDate);
  const endDate = toIsoEnd(portal.endDate);

  return {
    ...next,
    ...(stationId ? { stationId } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };
};

export const applyPortalToListFilter = (
  collection: string | undefined,
  filter: Record<string, unknown>,
  portal: TPortalFilter,
): Record<string, unknown> => {
  if (collection === 'stations') {
    const next = pickKeys(filter, ['statuses']);
    const station = {
      ...((filter.station as Record<string, unknown> | undefined) ?? {}),
    };

    if (portal.stationId?.length) {
      station.stationIds = portal.stationId;
    } else {
      delete station.stationIds;
    }

    if (Object.keys(station).length) {
      next.station = station;
    }

    return next;
  }

  if (collection === 'productCatalog') {
    const next = pickKeys(filter, [
      'search',
      'categories',
      'category',
      'shelfLocations',
      'shelfLocation',
    ]);
    // Every picked site goes to the catalog (one list request covers them all). Not first-only.
    const stationId = uniqueStationIds(portal.stationId);

    if (stationId) {
      next.stationId = stationId;
    }

    return next;
  }

  if ((LIST_COLLECTIONS_WITH_STATION_DATE as readonly string[]).includes(collection ?? '')) {
    const keys =
      LIST_FILTER_KEYS[collection as typeof LIST_COLLECTIONS_WITH_STATION_DATE[number]] ?? [];
    return overlayStationAndDates(pickKeys(filter, keys), portal);
  }

  return { ...filter };
};

/**
 * The sites the portal starts on when the operator saved no pick (first sign-in, after Reset,
 * after sign-out).
 *
 * What: the ids of the lookup row `set_default_stations` that exist among `openingSites`, else
 * the first site only.
 * Why among the opening sites only: they are the sites already loaded by the opening call, so the
 * default needs no extra request. Lookup ids that are not among them (other partners, or sites
 * past the first 12) are skipped.
 * Order: the opening-list order (the order of the sites on the server), not the lookup order, so
 * the result does not depend on how the lookup was typed.
 * Switch: only a `value` of "true" (case and spaces ignored) turns the row on; an inactive row
 * (`isActive === false`) is off. The row type lets `isActive` be absent, which counts as active.
 * Edge cases: lookup failed or `undefined`, row missing, `attributes` null or without a list,
 * empty list, no match: the first site only. No opening sites (partner with no sites): `[]`.
 */
export const getDefaultStationIds = (
  lookups: TLookup[] | null | undefined,
  openingSites: TStationListItem[],
): string[] => {
  const firstOnly = openingSites[0] ? [openingSites[0].id] : [];
  const row = lookups?.find(({ key }) => key === DEFAULT_STATIONS_LOOKUP_KEY);

  if (!row || row.isActive === false || String(row.value).trim().toLowerCase() !== 'true') {
    return firstOnly;
  }

  const listed =
    row.attributes && 'stations' in row.attributes && Array.isArray(row.attributes.stations)
      ? new Set(row.attributes.stations)
      : new Set<string>();
  const matched = openingSites.filter(({ id }) => listed.has(id)).map(({ id }) => id);

  return matched.length ? matched : firstOnly;
};

/**
 * Turns a pick of every site into "All sites" (`undefined`), so requests stay short however many
 * sites the partner has and a site added later is included.
 *
 * What: unique ids; `undefined` when there are none, or when their count equals `totalSites`.
 * Why a count: the picker no longer loads the full site list, so "does the pick name every site"
 * is answered by the total the opening call returned (`totalHits`).
 * Edge cases: `totalSites` 0 (unknown or the opening call failed) never converts; ids repeated in
 * the input count once; the order of the ids is kept for a narrower pick.
 */
export const normalizeStationPick = (
  stationId: string[] | undefined,
  totalSites: number,
): string[] | undefined => {
  const ids = uniqueStationIds(stationId);

  return ids && !(totalSites > 0 && ids.length === totalSites) ? ids : undefined;
};
